
import {Component, Vue} from "vue-property-decorator";
import {TRANSLATIONS_FETCH} from "@/store/ActionTypes";
import {mapState} from "vuex";
import CalculationsRepository from "@/utils/API/CalculationsRepository";
import CustomersRepository from "@/utils/API/CustomersRepository";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vTable from "@/components/vTable.vue";
import {NutrientFacts, RecipeBreakdown} from "@/models/Recipes/RecipeBreakdown";
import vSelect2 from "@/components/vSelect2.vue";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import vAminoAcidScoreReport from "@/components/reports/vAminoAcidScoreReport.vue";
import {round} from "../../utils/Helpers";
import vIconWithTooltip from "@/components/vIconWithTooltip.vue";
import vRecipeBreakdownTable from "@/components/vRecipeBreakdownTable.vue";
import {RecipeServingType} from "@/models/Recipes/RecipeServingType";

@Component({
  methods: {round},
  components: {
    vIconWithTooltip,
    vTable,
    vSelect2,
    vAminoAcidScoreReport,
    vRecipeBreakdownTable
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class Packages extends Vue {
  localizationState: any;

  recipesCalculation: RecipeBreakdown = null;
  nutrientFacts: NutrientFacts = null;
  customers: any[] = [];
  recipes: any[] = [];
  roundingRuleIndex: string = 'FDA';
  countryRules: any[] = [
    {
      name: "Canada",
      id: 0,
    },
    {
      name: "US",
      id: 1,
    },
    {
      name: "UK",
      id: 2,
    },
  ];
  filter: any = {
    isChosen: false,
    selectedCustomer: null,
    recipe: {
      selectedRecipe: null,
      name: "",
      disabled: true,
    },
    selectedCountry: 0,
  };

  overrideWeightDisplay(valueIfNotOverridden: number): string {
    return `${this.nutrientFacts.overrideWeightPerServing || valueIfNotOverridden}${this.nutrientFacts.overrideUnit || "g"}`;
  }

  get tableHeaders(): string[] {
    const staticHeaders = [
      'Ingredient',
      'Quantity',
      'Quantity (G)'
    ];

    const dynamicHeaders = Object.keys(this.nutrientFacts.ingredients[0])
      .filter((key) =>
        key !== 'ingredientName' &&
        key !== 'quantity' &&
        key !== 'quantityInGrams' &&
        key !== 'quantityInGramsAfterLoss' &&
        key !== 'childRecipeBreakdown'
      );

    // HACK: Added sugar has a naming convention mismatch
    const addedSugarIndex = dynamicHeaders.indexOf('addedSugar');
    if (addedSugarIndex > 0) {
      dynamicHeaders[addedSugarIndex] = 'addedSugar';
    }

    return [...staticHeaders, ...dynamicHeaders];
  }

  get slicedTableHeaders():string[]{
    return this.tableHeaders.slice(5);
  }

  nutrientRoundingRulesData() {
    const rows = [];
    rows.push({
      name: 'Quantity(G)',
      dailyValueRule: '',
      roundingRule:'',
      dailyValueRoundingRule:'',
      total: this.recipesCalculation.nutrientFacts.rawWeight.value + this.recipesCalculation.nutrientFacts.rawWeight.unit,
      totalPerServings: (
        this.recipesCalculation.nutrientFacts.rawWeightPerServing.value
        + this.recipesCalculation.nutrientFacts.rawWeightPerServing.unit
        + ` (${this.overrideWeightDisplay(this.recipesCalculation.nutrientFacts.rawWeightPerServing.value)})`
      ),
    });
    rows.push({
      name: 'Quantity(G) With Loss',
      dailyValueRule:'',
      roundingRule:'',
      dailyValueRoundingRule:'',
      total: this.recipesCalculation.nutrientFacts.weight.value + this.recipesCalculation.nutrientFacts.weight.unit,
      totalPerServings: (
        this.recipesCalculation.nutrientFacts.weightPerServing.value
        + this.recipesCalculation.nutrientFacts.weightPerServing.unit
        + ` (${this.overrideWeightDisplay(this.recipesCalculation.nutrientFacts.weightPerServing.value)})`
      ),
    });

    for (const key in this.recipesCalculation) {
      if (this.recipesCalculation[key] != null) {
        if (this.recipesCalculation[key].absoluteValue != null) {
          rows.push({
            name: key + ' ('+this.recipesCalculation[key].absoluteValue.unit +')',
            isOverriden: this.recipesCalculation[key].isOverridden,
            dailyValueRule: this.recipesCalculation[key].dailyValueRule,
            roundingRule: this.recipesCalculation[key].roundingRule,
            dailyValueRoundingRule: this.recipesCalculation[key].dailyValueRoundingRule,
            total: this.recipesCalculation[key].absoluteValue.value + this.recipesCalculation[key].absoluteValue.unit,
            totalPerServings : this.recipesCalculation[key].absoluteValuePerServing.value + this.recipesCalculation[key].absoluteValuePerServing.unit,
          });
        }
      }
    }

    return rows;
  }

  get translations() {
    return this.localizationState.translations;
  }

  // Load recipes
  loadRecipes() {
    RecipesRepository.listRestaurantRecipesAsLookups(this.filter.selectedCustomer)
      .then((response) => {
        this.recipes = response.data.result;
        this.filter.recipe.disabled = false;
        if (this.recipes.length > 0) {
          this.filter.recipe.selectedRecipe = this.recipes[0].id;
          this.filter.recipe.name = this.recipes[0].name;
          this.viewLabel();
        }
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  // Load customers
  loadCustomers() {
    CustomersRepository.listRestaurantsAsLookups()
      .then((response) => {
        this.customers = response.data.result;
        if (this.customers.length) {
          this.filter.selectedCustomer = this.customers[0].id;
          this.loadRecipes();
        }
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  // Load recipe calculations
  viewLabel() {
    const model = {
      recipeId: this.filter.recipe.selectedRecipe,
      country: this.filter.selectedCountry,
    };

    // @ts-ignore
    this.$snotify.async(
      this.translations.get("calculations_view_label_loading"),
      () =>
        new Promise((success) => {
          CalculationsRepository.getRecipeCalculationBreakdown(model)
            .then((response) => {
              this.filter.isChosen = true;
              this.recipesCalculation = response.data.result;
              if (response.data.result.nutrientFacts) {
                this.nutrientFacts = response.data.result.nutrientFacts;
              }
              this.getRecipeMetaData(model.recipeId);
              success({
                body: this.translations.get("calculations_view_label_success"),
              });
            })
            .catch((error) => {
              if (error.response)
                this.$snotify.error(error.response.errorMessages[0].userVisibleMessage);
              else {
                this.$snotify.error(this.translations.get("network-error"));
              }
            });
        }),
      {
        timeout: 2500,
        closeOnClick: true,
      }
    );
  }

  getRecipeMetaData(recipeId: number){
    RecipesRepository.getRecipeMetaData(recipeId)
      .then((response: any) => {
        this.roundingRuleIndex = response.data.roundingRuleIndex === 0 ? 'FDA' : 'FSIS';
      })
      .catch(() => {
      });
  }

  getAdditionalLabelInformation(key: LabelTypeMetadata): any | null {
    if (key in this.recipesCalculation.additionalLabelInformation) {
      return this.recipesCalculation.additionalLabelInformation[key];
    }

    return null;
  }

  getHighInMetadata() {
    const additionalLabelInformation = this.getAdditionalLabelInformation(LabelTypeMetadata.FrontOfPack);

    if (additionalLabelInformation) {
      return additionalLabelInformation.highInMetadata;
    }

    return 0;
  }

  selectRecipe(id: any) {
    this.filter.recipe.selectedRecipe = id;
    const recipe = this.recipes.find((x) => x.id == id);
    this.filter.recipe.name = recipe.name;
    this.filter.isChosen = false;
    this.viewLabel();
  }
  selectCustomer(id: any) {
    this.filter.selectedCustomer = id;
    this.filter.recipe.disabled = false;
    this.filter.isChosen = false;
    this.loadRecipes();
  }
  selectCountry(id: any) {
    this.filter.selectedCountry = id;
    this.filter.isChosen = false;
    this.viewLabel();
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "calculations", "ingredients"],
    });
    document.title = "Calculations | MenuSano";
    this.loadCustomers();
  }
}
export default Packages;
